/**
 * Copy and the refusal vocabulary for `/stablecoins/invoices/resolutions`
 * (page spec `invoice-resolution-approval`, ALL-5908).
 *
 * The refusal sentences here name the codes the merged issuer contract and the
 * invoicing client can actually answer with for a decision:
 *
 *   - the four-eyes and identity refusals the contract documents on
 *     `POST /v1/invoice-resolutions/{resolutionId}/approve`
 *     (`APPROVER_MUST_DIFFER_FROM_PROPOSER`,
 *     `MACHINE_PRINCIPAL_NOT_AN_APPROVER`), plus the fresh step-up it requires
 *     (`AUTH_STEP_UP_REQUIRED`);
 *   - the state and concurrency refusals (`RESOLUTION_NOT_PENDING`,
 *     `VERSION_CONFLICT` — the page spec's "proposal_changed_since_review");
 *   - the transport codes the api module declares (NOT_FOUND,
 *     VALIDATION_FAILED, IDEMPOTENCY_KEY_REUSED, FORBIDDEN,
 *     DEPENDENCY_UNAVAILABLE) and the two it mints when no body can be read
 *     (`NETWORK_ERROR`, `MALFORMED_RESPONSE`).
 *
 * A code outside that list is shown as itself, bounded, never paraphrased.
 */

export const RESOLUTIONS_COPY = {
  title: "Resolution approvals",
  subtitle:
    "Independent review of a proposed invoice resolution or refund before it is authorised.",
  loadingLabel: "Loading proposed resolutions",
  queueHeading: "Proposed resolutions",
  queueNote:
    "Every proposal below was raised by a different principal and needs an independent approver. Opening one shows its evidence packet.",
  queueEmptyTitle: "Nothing is waiting for approval",
  queueEmptyBody:
    "No exception is currently awaiting a resolution decision. A proposal appears here as soon as another principal raises one.",
  packetHeading: "Evidence packet",
  selectPrompt: "Select a proposal to read its evidence packet before deciding.",
  resolutionColumn: "Proposal",
  exceptionColumn: "Exception",
  kindColumn: "Disposition",
  amountColumn: "Amount",
  stateColumn: "State",
  openLabel: "Review",
  /** The packet's blocks. */
  exceptionSection: "Exception",
  paymentSection: "Constituent payments",
  proposalSection: "Proposed disposition",
  policySection: "Policy and risk",
  auditSection: "Audit trail",
  refundSection: "Refund",
  exceptionIdLabel: "Exception",
  invoiceLabel: "Invoice",
  paymentIdLabel: "Payment",
  proposedByLabel: "Proposed by",
  approvedByLabel: "Approved by",
  decidedAtLabel: "Decided",
  createdAtLabel: "Proposed at",
  reasonLabel: "Argument",
  noReason: "No argument was recorded with this proposal.",
  policyRefLabel: "Policy evaluation",
  noPolicyRef: "No policy evaluation is attached to this proposal.",
  targetInvoiceLabel: "Applied to invoice",
  amountBaseUnitsLabel: "Base units",
  amountDisplayLabel: "Amount",
  paymentsEmptyTitle: "No payments are recorded against this invoice",
  paymentsEmptyBody:
    "The invoice this exception belongs to has no payment rows, so no constituent payment can be shown.",
  refundStateLabel: "Refund state",
  refundDestinationLabel: "Destination",
  refundNoDestination: "No destination address yet — the refund waits on a payer-confirmed address.",
  refundConfirmationLabel: "Confirmation",
  refundScreeningLabel: "Destination screening",
  refundWithdrawalLabel: "Withdrawal intent",
  /** The decision controls. */
  approveLabel: "Approve",
  approvingLabel: "Approving…",
  rejectLabel: "Reject",
  rejectingLabel: "Rejecting…",
  cancelLabel: "Withdraw proposal",
  cancellingLabel: "Withdrawing…",
  rejectReasonLabel: "Reason for rejection",
  rejectReasonHint: "Required. The proposer sees this reason.",
  rejectReasonRequired: "Enter a reason before rejecting this proposal.",
  decisionNote:
    "Approving needs a recent multi-factor sign-in and is refused unless the approver is a different human principal from the proposer.",
  approvedToast: "Resolution approved.",
  rejectedToast: "Resolution rejected.",
  withdrawnToast: "Proposal withdrawn.",
  decidedElsewhere:
    "This proposal is no longer awaiting a decision, so it has no decision controls. Reload to see its current state.",
  /** The distinct step-up failure: the session could not satisfy step-up at all. */
  stepUpFailedTitle: "This decision needs a recent multi-factor sign-in",
  stepUpFailedBody:
    "The step-up was refused for this session, so the decision was not recorded. Sign out, sign in again with MFA, then decide again. Nothing is retried automatically.",
  stepUpAttemptedNote:
    "A step-up was attempted for this decision; it did not succeed, so nothing changed.",
  /** Read-only framing for a principal that may not decide. */
  readonlyHint:
    "Deciding a proposal needs the invoice:resolve.approve permission (or invoice:refund.approve for a refund). A machine principal is never an approver.",
  refusals: {
    APPROVER_MUST_DIFFER_FROM_PROPOSER:
      "This proposal was raised by the same principal that is trying to approve it, so the four-eyes rule refused the decision. A different human approver must decide it.",
    MACHINE_PRINCIPAL_NOT_AN_APPROVER:
      "A machine principal can propose but can never approve, even when its key carries an approve-looking scope. A human approver must decide this proposal.",
    AUTH_STEP_UP_REQUIRED:
      "The service requires a fresh step-up for this decision and the session could not satisfy it, so nothing was recorded.",
    RESOLUTION_NOT_PENDING:
      "This proposal is no longer pending — it was decided or withdrawn elsewhere. Reload to see its current state.",
    VERSION_CONFLICT:
      "This proposal changed since you opened it, so the decision was refused. Reload and review the current proposal before deciding again.",
    FORBIDDEN:
      "This principal may not decide this proposal. An organization admin can grant invoice:resolve.approve (or invoice:refund.approve for a refund).",
    VALIDATION_FAILED: "The service refused this request as it was sent. Nothing was recorded.",
    IDEMPOTENCY_KEY_REUSED:
      "This decision was refused because the same request key arrived with a different body. Reload and decide once.",
    DEPENDENCY_UNAVAILABLE:
      "A service this decision depends on did not answer. Nothing was recorded; try again.",
    NOT_FOUND: "The proposal could not be found. It may have been withdrawn.",
    NETWORK_ERROR:
      "The invoicing service could not be reached, so no proposal on this page is live. Nothing was recorded.",
    MALFORMED_RESPONSE:
      "The invoicing service answered with a body this client could not read. Nothing on this page can be trusted until it answers correctly.",
  } as Record<string, string>,
} as const;

/** True for a code that means "you may not do this", as opposed to "it failed". */
export function isDeniedCode(code: string): boolean {
  return code === "FORBIDDEN" || code === "HTTP_401" || code === "HTTP_403";
}

/** True for the specific step-up refusal the decision routes answer with. */
export function isStepUpCode(code: string): boolean {
  return code === "AUTH_STEP_UP_REQUIRED";
}
